import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    getOrderedVisibleColumnIds,
    normalizeVisiblePrintColumns,
    PRINT_COLUMN_DEFINITION_MAP,
    PRINT_COLUMN_DEFINITIONS,
    PrintColumnId,
    PrintLabels,
    PrintReservationRow,
    PrintRowDensity,
    PrintSortDirection,
    PrintSortKey,
    PrintTableConfig,
    reorderPrintColumns,
    resizePrintColumns,
    setVisiblePrintColumns
} from "../../utils/print-utils";

interface Props {
    labels: PrintLabels;
    config: PrintTableConfig;
    rows: PrintReservationRow[];
    onConfigChange: (config: PrintTableConfig) => void;
    onReset: () => void;
}

const ROW_HEIGHTS = {
    compact: 42,
    standard: 54,
    comfortable: 68,
} as const;

const SortableHeaderCell: React.FC<{
    id: PrintColumnId;
    label: string;
    width: number;
    isMobile: boolean;
    showResizeHandle: boolean;
    onResizeStart: (event: React.MouseEvent<HTMLDivElement>, id: PrintColumnId) => void;
}> = ({id, label, width, isMobile, showResizeHandle, onResizeStart}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});

    return (
        <Box
            ref={setNodeRef}
            sx={{
                width: `${width}%`,
                flexShrink: 0,
                position: 'relative',
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.72 : 1,
                bgcolor: 'grey.50',
                borderRight: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                title={label}
                sx={{
                    px: isMobile ? 0.75 : 1.25,
                    py: isMobile ? 0.75 : 1,
                    display: 'flex',
                    alignItems: 'flex-end',
                    height: '100%',
                    gap: 0.75,
                    cursor: 'grab',
                    '&:active': {cursor: 'grabbing'},
                    userSelect: 'none',
                    minWidth: 0,
                }}
            >
                <DragIndicatorIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                <Typography
                    variant="caption"
                    noWrap
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        fontSize: isMobile ? '0.68rem' : undefined,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {label}
                </Typography>
            </Box>

            {showResizeHandle && (
                <Box
                    onMouseDown={(event) => onResizeStart(event, id)}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: -5,
                        bottom: 0,
                        width: 10,
                        zIndex: 2,
                        cursor: 'col-resize',
                    }}
                />
            )}
        </Box>
    );
};

const PrintTablePanel: React.FC<Props> = ({
    labels,
    config,
    rows,
    onConfigChange,
    onReset,
}) => {
    const {t} = useTranslation();
    const isMobile = useMediaQuery('(max-width:900px)');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const resizeRef = useRef<{
        startX: number;
        leftId: PrintColumnId;
        rightId: PrintColumnId;
        columns: PrintTableConfig['columns'];
    } | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const visibleColumnDefinitions = useMemo(() => {
        const orderedVisibleIds = new Set(getOrderedVisibleColumnIds(config.columns));
        return PRINT_COLUMN_DEFINITIONS.filter((definition) => orderedVisibleIds.has(definition.id));
    }, [config.columns]);
    const visibleColumns = useMemo(() => normalizeVisiblePrintColumns(config.columns), [config.columns]);
    const rowHeight = ROW_HEIGHTS[config.rowDensity];

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    const sortOptions: Array<{value: PrintSortKey; label: string}> = [
        {value: 'start', label: labels.start},
        {value: 'roomNumber', label: labels.roomNumber},
        {value: 'tables', label: labels.table},
        {value: 'guestLastName', label: labels.guest},
    ];
    const sortDirectionOptions: Array<{value: PrintSortDirection; label: string}> = [
        {value: 'asc', label: t('Ascending')},
        {value: 'desc', label: t('Descending')},
    ];
    const rowDensityOptions: Array<{value: PrintRowDensity; label: string}> = [
        {value: 'compact', label: t('Compact')},
        {value: 'standard', label: t('Standard')},
        {value: 'comfortable', label: t('Comfortable')},
    ];

    useEffect(() => {
        const updateWidth = () => {
            setContainerWidth(containerRef.current?.clientWidth ?? 0);
        };

        updateWidth();
        const observer = typeof ResizeObserver !== 'undefined' && containerRef.current
            ? new ResizeObserver(() => updateWidth())
            : null;

        if (observer && containerRef.current) {
            observer.observe(containerRef.current);
        }

        window.addEventListener('resize', updateWidth);
        return () => {
            window.removeEventListener('resize', updateWidth);
            observer?.disconnect();
        };
    }, []);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!resizeRef.current) {
                return;
            }

            onConfigChange({
                ...config,
                columns: resizePrintColumns(
                    resizeRef.current.columns,
                    resizeRef.current.leftId,
                    resizeRef.current.rightId,
                    event.clientX - resizeRef.current.startX,
                    containerWidth,
                ),
            });
        };
        const stopResize = () => {
            resizeRef.current = null;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopResize);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopResize);
        };
    }, [config, containerWidth, onConfigChange]);

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id) {
            return;
        }

        onConfigChange({
            ...config,
            columns: reorderPrintColumns(config.columns, String(active.id), String(over.id)),
        });
    };

    return (
        <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="subtitle2">{t('Table layout')}</Typography>
                <Button variant="text" size="small" onClick={onReset}>
                    {t('Reset to defaults')}
                </Button>
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                <FormControl size="small" fullWidth>
                    <InputLabel>{t('Sort by')}</InputLabel>
                    <Select
                        label={t('Sort by')}
                        value={config.sortBy}
                        onChange={(event) => {
                            onConfigChange({
                                ...config,
                                sortBy: event.target.value as PrintSortKey,
                            });
                        }}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('Sort direction')}</InputLabel>
                    <Select
                        label={t('Sort direction')}
                        value={config.sortDirection}
                        onChange={(event) => {
                            onConfigChange({
                                ...config,
                                sortDirection: event.target.value as PrintSortDirection,
                            });
                        }}
                    >
                        {sortDirectionOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <FormControl size="small" fullWidth>
                <InputLabel>{t('Row height')}</InputLabel>
                <Select
                    label={t('Row height')}
                    value={config.rowDensity}
                    onChange={(event) => {
                        onConfigChange({
                            ...config,
                            rowDensity: event.target.value as PrintRowDensity,
                        });
                    }}
                >
                    {rowDensityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Autocomplete
                multiple
                disableCloseOnSelect
                options={PRINT_COLUMN_DEFINITIONS}
                value={visibleColumnDefinitions}
                getOptionLabel={(option) => labels[option.labelKey]}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_event, nextValue) => {
                    onConfigChange({
                        ...config,
                        columns: setVisiblePrintColumns(config.columns, nextValue.map((option) => option.id)),
                    });
                }}
                renderOption={(props, option, {selected}) => (
                    <li {...props} key={option.id} style={{...props.style, paddingTop: 4, paddingBottom: 4}}>
                        <Checkbox checked={selected} sx={{mr: 1}}/>
                        <span>{labels[option.labelKey]}</span>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('Print columns')}
                        size="small"
                    />
                )}
            />

            <Box>
                <Typography variant="caption" sx={{display: 'block', color: 'text.secondary', mb: 1}}>
                    {t('Print columns')}
                </Typography>

                <Paper elevation={0} sx={{p: 0, bgcolor: 'transparent'}}>
                    <Box ref={containerRef}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={visibleColumns.map((column) => column.id)} strategy={horizontalListSortingStrategy}>
                                <Box sx={{display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden'}}>
                                    {visibleColumns.map((column, index) => (
                                        <SortableHeaderCell
                                            key={column.id}
                                            id={column.id}
                                            label={labels[PRINT_COLUMN_DEFINITION_MAP[column.id].labelKey]}
                                            width={column.normalizedWidth}
                                            isMobile={isMobile}
                                            showResizeHandle={index < visibleColumns.length - 1}
                                            onResizeStart={(event, id) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                const rightColumn = visibleColumns[index + 1];
                                                if (!rightColumn) {
                                                    return;
                                                }
                                                resizeRef.current = {
                                                    startX: event.clientX,
                                                    leftId: id,
                                                    rightId: rightColumn.id,
                                                    columns: config.columns.map((item) => ({...item})),
                                                };
                                            }}
                                        />
                                    ))}
                                </Box>
                            </SortableContext>
                        </DndContext>

                        <Stack
                            spacing={0}
                            sx={{
                                border: '1px solid',
                                borderTop: 'none',
                                borderColor: 'divider',
                                borderBottomLeftRadius: 1,
                                borderBottomRightRadius: 1,
                                overflow: 'hidden',
                                height: {xs: 240, sm: 280},
                                overflowY: 'auto',
                            }}
                        >
                            {rows.map((row, rowIndex) => (
                                <Box key={`${row.guestLastName}-${row.roomNumber}-${rowIndex}`} sx={{display: 'flex', minHeight: rowHeight, bgcolor: rowIndex % 2 === 0 ? 'common.white' : 'grey.50'}}>
                                    {visibleColumns.map((column) => {
                                        let cellValue = '';
                                        let flagUrl = '';
                                        switch (column.id) {
                                            case 'guest':
                                                cellValue = row.guestDisplayName || row.guestFullName || '';
                                                break;
                                            case 'status':
                                                cellValue = row.status;
                                                break;
                                            case 'nationality':
                                                cellValue = row.nationalityLabel;
                                                flagUrl = row.nationalityFlagUrl;
                                                break;
                                            case 'stayCount':
                                                cellValue = row.stayCount;
                                                break;
                                            case 'stay':
                                                cellValue = [row.stayStart, row.stayEnd].filter(Boolean).join('\n');
                                                break;
                                            case 'roomNumber':
                                                cellValue = row.roomDisplay || row.roomNumber || '';
                                                break;
                                            case 'rateCode':
                                                cellValue = row.rateCode;
                                                break;
                                            case 'allergiesDiets':
                                                cellValue = row.allergiesDiets;
                                                break;
                                            case 'table':
                                                cellValue = row.tables;
                                                break;
                                            case 'start':
                                                cellValue = row.start;
                                                break;
                                            case 'end':
                                                cellValue = row.end;
                                                break;
                                            case 'guests':
                                                cellValue = row.guests;
                                                break;
                                            case 'total':
                                                cellValue = row.total;
                                                break;
                                            case 'ageCategories':
                                                cellValue = row.ageCategories;
                                                break;
                                            case 'aliasAgeCategories':
                                                cellValue = row.aliasAgeCategories;
                                                break;
                                            case 'notes':
                                                cellValue = row.notes;
                                                break;
                                        }
                                        const nonBreakingItems = column.id === 'aliasAgeCategories' || column.id === 'rateCode'
                                            ? cellValue.split(/\s*,\s*/).filter(Boolean)
                                            : [];

                                        return (
                                            <Box
                                                key={`${column.id}-${rowIndex}`}
                                                sx={{
                                                    width: `${column.normalizedWidth}%`,
                                                    flexShrink: 0,
                                                    px: isMobile ? 0.75 : 1.25,
                                                    py: isMobile ? 0.75 : 1,
                                                    borderRight: '1px solid',
                                                    borderColor: 'divider',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: flagUrl ? 'center' : 'flex-start',
                                                    minWidth: 0,
                                                }}
                                            >
                                                {flagUrl ? (
                                                    <Box
                                                        component="img"
                                                        alt={cellValue}
                                                        title={cellValue || undefined}
                                                        src={flagUrl}
                                                        sx={{
                                                            width: 14,
                                                            height: 14,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            border: '0.5px solid rgba(0,0,0,0.15)',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        component="div"
                                                        variant="caption"
                                                        title={cellValue || undefined}
                                                        sx={{
                                                            lineHeight: 1.25,
                                                            color: 'text.primary',
                                                            display: column.id === 'guest' ? 'block' : '-webkit-box',
                                                            WebkitLineClamp: column.id === 'guest' ? undefined : 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            wordBreak: nonBreakingItems.length ? 'normal' : 'break-word',
                                                            whiteSpace: column.id === 'stay' || column.id === 'guest' ? 'pre-line' : undefined,
                                                            minHeight: '1em',
                                                            fontSize: isMobile ? '0.68rem' : undefined,
                                                        }}
                                                    >
                                                        {nonBreakingItems.length > 0
                                                            ? nonBreakingItems.map((item, index) => (
                                                                <React.Fragment key={`${item}-${index}`}>
                                                                    {index > 0 && ', '}
                                                                    <Box component="span" sx={{whiteSpace: 'nowrap'}}>
                                                                        {item}
                                                                    </Box>
                                                                </React.Fragment>
                                                            ))
                                                            : cellValue}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                            {rows.length === 0 && (
                                <Box sx={{minHeight: rowHeight, px: isMobile ? 0.75 : 1.25, py: 1.5, color: 'text.secondary'}}>
                                    <Typography variant="caption">
                                        {t('No preview rows available')}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Paper>
            </Box>
        </Stack>
    );
};

export default PrintTablePanel;
